/**
 * Плитки картинок песочницы: превью, комментарий для модели, «убрать»; зона drop и кнопка выбора файла.
 * Одна и та же у панели «Референсы» (вставленные картинки) и у платьев (загрузки фото платья).
 * `checkable` — у загрузок платья есть галочка «идёт в запуск»; у референсов картинка всегда включена.
 * `roles` — переключатель роли «подсказка | внешность модели» (только у вставленных: загрузка платья
 * идёт во входы товаром, роли у неё нет).
 */
import { ImagePlus, X } from "lucide-react";
import { useRef, useState, type ReactNode } from "react";

import { btn, ErrorLine, label } from "@/components/trends/ui";
import { mediaUrl } from "@/lib/media";
import { COMMENT_MAX, imageFiles, toPic, uploadImages, type Pic, type PicRole } from "@/lib/uploads";

const HINT = "без комментария модель сама решит, что взять";

/** Подписи ролей вставленной картинки — в том же порядке, в каком их показывает переключатель. */
const ROLE_LABELS: { value: PicRole; label: string }[] = [
  { value: "hint", label: "подсказка" },
  { value: "portrait", label: "внешность модели" },
];
/** Что роль делает с промптом — подсказка у переключателя. */
const ROLE_HINT =
  "подсказка: что взять с картинки словами комментария · внешность модели: лицо, причёска и типаж берутся с неё, одежда и фон — нет";
/** У портрета подсказку модели пишет сервис (по-английски), комментарий сотрудника в промпт не идёт. */
const PORTRAIT_COMMENT_HINT = "заметка для себя: у портрета подсказку модели пишет сервис";
const roleSelect =
  "h-6 w-full rounded-[var(--radius-ui)] border border-border bg-bg px-1 text-[11.5px] text-fg outline-none focus:border-accent";

/**
 * Зона drop: вся область потомков принимает файлы-картинки. Счётчик `dragenter`/`dragleave` — чтобы
 * подсветка не мигала при заходе на дочерние элементы; не-файловое перетаскивание игнорируется.
 */
export function DropZone({ onFiles, children }: { onFiles: (files: File[]) => void; children: ReactNode }) {
  const [depth, setDepth] = useState(0);
  const isFiles = (e: React.DragEvent) => Array.from(e.dataTransfer.types).includes("Files");
  return (
    <div
      className={depth > 0 ? "rounded-[var(--radius-ui)] ring-2 ring-accent" : undefined}
      onDragEnter={(e) => {
        if (isFiles(e)) setDepth((d) => d + 1);
      }}
      onDragOver={(e) => {
        if (isFiles(e)) e.preventDefault();
      }}
      onDragLeave={(e) => {
        if (isFiles(e)) setDepth((d) => Math.max(0, d - 1));
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDepth(0);
        const files = imageFiles(e.dataTransfer.files);
        if (files.length) onFiles(files);
      }}
    >
      {children}
    </div>
  );
}

export function PicTiles({
  pics,
  onChange,
  onFiles,
  checkable = false,
  roles = false,
  hint,
  placeholder = "что взять отсюда",
  emptyHint = HINT,
  altPrefix = "картинка",
  children,
}: {
  pics: Pic[];
  onChange: (next: Pic[]) => void;
  /** Новые файлы (выбор): родитель грузит и добавляет сам; drop принимает `DropZone` вокруг. */
  onFiles: (files: File[]) => void;
  checkable?: boolean;
  /** Показывать переключатель роли (подсказка / внешность модели) — только у вставленных картинок. */
  roles?: boolean;
  hint?: ReactNode;
  /** Подсказка пустого комментария и пояснение, что будет без него. */
  placeholder?: string;
  emptyHint?: string;
  altPrefix?: string;
  children?: ReactNode;
}) {
  const pick = useRef<HTMLInputElement>(null);
  const set = (i: number, patch: Partial<Pic>) => onChange(pics.map((p, k) => (k === i ? { ...p, ...patch } : p)));

  return (
    <div className="grid gap-2 rounded-[var(--radius-ui)] border border-dashed border-border p-2" data-annot="картинки">
      {pics.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {pics.map((p, i) => (
            <div
              key={p.asset_id}
              className={`grid w-[120px] gap-1 text-[11.5px] ${checkable && !p.on ? "opacity-50" : ""}`}
              data-annot="плитка картинки"
            >
              <div className="relative">
                <img
                  src={mediaUrl(p.path)}
                  alt={p.comment.trim() || `${altPrefix} ${i + 1}`}
                  className="aspect-[3/4] w-full rounded-[4px] border border-border object-cover"
                />
                {checkable ? (
                  <input
                    type="checkbox"
                    className="absolute top-1 left-1"
                    title="отправить в запуск"
                    aria-label={`${altPrefix} ${i + 1}: отправить в запуск`}
                    checked={p.on}
                    onChange={() => set(i, { on: !p.on })}
                  />
                ) : null}
                <button
                  type="button"
                  title="убрать"
                  aria-label={`убрать: ${altPrefix} ${i + 1}`}
                  onClick={() => onChange(pics.filter((_, k) => k !== i))}
                  className="absolute top-1 right-1 grid size-5 place-items-center rounded-full bg-bg/80 text-fg-muted hover:text-err"
                >
                  <X size={12} />
                </button>
              </div>
              {p.width && p.height ? (
                <div className="tnum text-fg-faint">
                  {p.width}×{p.height}
                </div>
              ) : null}
              {roles ? (
                <select
                  className={roleSelect}
                  title={ROLE_HINT}
                  aria-label={`роль: ${altPrefix} ${i + 1}`}
                  value={p.role ?? "hint"}
                  onChange={(e) => set(i, { role: e.target.value as PicRole })}
                >
                  {ROLE_LABELS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              ) : null}
              <textarea
                className="min-h-[44px] w-full resize-y rounded-[var(--radius-ui)] border border-border bg-bg px-1.5 py-1 text-[11.5px] text-fg outline-none focus:border-accent"
                placeholder={p.role === "portrait" ? PORTRAIT_COMMENT_HINT : placeholder}
                title={p.role === "portrait" ? PORTRAIT_COMMENT_HINT : emptyHint}
                aria-label={`комментарий: ${altPrefix} ${i + 1}`}
                value={p.comment}
                maxLength={COMMENT_MAX}
                onChange={(e) => set(i, { comment: e.target.value })}
              />
            </div>
          ))}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={btn} onClick={() => pick.current?.click()}>
          <ImagePlus size={13} />
          Добавить картинки
        </button>
        <input
          ref={pick}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          multiple
          hidden
          data-annot="выбор файлов"
          onChange={(e) => {
            const files = imageFiles(e.target.files);
            e.target.value = "";
            if (files.length) onFiles(files);
          }}
        />
        <span className={label}>
          {hint ?? "перетащите сюда или вставьте Ctrl+V"} · {emptyHint}
        </span>
        {children}
      </div>
    </div>
  );
}

/** Загрузка файлов: готовые картинки — в `onItems`, сбой запроса и отказы по файлам — в `status`. */
export function useUploader(onItems: (items: Pic[]) => void, onBusy?: (delta: 1 | -1) => void) {
  const [busy, setBusy] = useState(0);
  // `upload` дочитывает ответ после await: берём свежий обработчик, а не тот, что был при старте
  const latest = useRef({ onItems, onBusy });
  latest.current = { onItems, onBusy };
  const [error, setError] = useState<unknown>(null);
  const [rejected, setRejected] = useState<string[]>([]);
  const upload = async (files: File[]) => {
    if (!files.length) return;
    setBusy((n) => n + 1);
    latest.current.onBusy?.(1);
    setError(null);
    try {
      const got = await uploadImages(files);
      setRejected(got.errors);
      latest.current.onItems(got.items.map(toPic));
    } catch (e) {
      setError(e);
    } finally {
      setBusy((n) => n - 1);
      latest.current.onBusy?.(-1);
    }
  };
  const status = (
    <>
      {busy > 0 ? <div className={label}>загружаю…</div> : null}
      <ErrorLine error={error} />
      {rejected.length ? (
        <ul className="text-[12px] text-err">
          {rejected.map((r, i) => (
            <li key={i}>{r}</li>
          ))}
        </ul>
      ) : null}
    </>
  );
  return { upload, status };
}
